/**
 * @fileoverview Единая точка загрузки ONNX Runtime Web
 * @module MikhmelViewer/Cinematic10/OrtLoader
 *
 * Повторяет поведение _ensureOrt() из src/ai/aiModule.js: сначала пробуем
 * сборку с WebGPU-провайдером, при неудаче откатываемся на WASM. Путь к
 * wasm-артефактам и число потоков задаются так же, чтобы рантайм в проекте
 * был один и тот же — иначе в бандл попадут две копии по ~129 МБ, и
 * globIgnores: ['ort-wasm/**'] в vite.config.js перестанет спасать.
 *
 * Загрузка ленивая: пока не понадобится нейросетевой денойзинг или
 * ONNX-модель автопресетов, пакет не подтягивается вообще.
 */

let _ort = null;
let _ortPromise = null;
let _backend = null;

let _log = { debug: () => {}, info: () => {}, warn: () => {}, error: () => {} };
export function setOrtLogger(logger) { if (logger) _log = logger; }

/**
 * @returns {Promise<object|null>} объект ort либо null, если рантайм недоступен
 */
export async function ensureOrt() {
  if (_ort) return _ort;
  if (_ortPromise) return _ortPromise;

  _ortPromise = (async () => {
    // [10.19.24] Выбор движка в панели ИИ (ai-backend) раньше был пустышкой:
    // список рисовался, а рантайм всегда пробовал WebGPU. Теперь сохранённый
    // выбор читается ЗДЕСЬ — в единственном месте, где рантайм поднимается.
    // «wasm» означает: WebGPU не пробовать вовсе (на части машин его попытка
    // стоит секунд и заканчивается откатом — врач вправе это выключить).
    let выбор = null;
    try { выбор = (typeof localStorage !== 'undefined') ? localStorage.getItem('mikhmel.ortBackend') : null; } catch (e) { выбор = null; }
    if (выбор === 'wasm') {
      try {
        const m2 = await import('onnxruntime-web');
        _ort = m2.default || m2;
        _configure(_ort);
        _backend = 'wasm';
        _log.debug('[Cinematic/ONNX] Рантайм загружен с провайдером WASM (выбран в панели ИИ)');
      } catch (e2) {
        _log.error('[Cinematic/ONNX] Рантайм недоступен:', e2.message);
        _ort = null;
        _backend = null;
      }
      return _ort;
    }
    try {
      const m = await import('onnxruntime-web/webgpu');
      _ort = m.default || m;
      _configure(_ort);
      _backend = 'webgpu';
      _log.debug('[Cinematic/ONNX] Рантайм загружен с провайдером WebGPU');
    } catch (e) {
      _log.warn('[Cinematic/ONNX] Сборка с WebGPU недоступна, откат на WASM:', e.message);
      try {
        const m2 = await import('onnxruntime-web');
        _ort = m2.default || m2;
        _configure(_ort);
        _backend = 'wasm';
        _log.debug('[Cinematic/ONNX] Рантайм загружен с провайдером WASM');
      } catch (e2) {
        _log.error('[Cinematic/ONNX] Рантайм недоступен:', e2.message);
        _ort = null;
        _backend = null;
      }
    }
    return _ort;
  })();

  return _ortPromise;
}

function _configure(ort) {
  if (!ort?.env?.wasm) return;
  ort.env.wasm.wasmPaths = '/ort-wasm/';
  ort.env.wasm.numThreads = Math.min(
    (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4, 4
  );
}

/** Список провайдеров для InferenceSession в порядке предпочтения. */
export function getExecutionProviders() {
  return _backend === 'webgpu' ? ['webgpu', 'wasm'] : ['wasm'];
}

export function getOrt() { return _ort; }
export function getOrtBackend() { return _backend; }
export function isOrtLoaded() { return !!_ort; }
